import { Data, Model } from "@singularsystems/neo-core";
import { AxiosPromise } from "axios";
import { injectable } from "inversify";
import { AppService, Types } from "../../App/Services/AppService";
import CommandResult from "../../Common/Models/CommandResult";
import StaticServiceProviderImportDatesLookup from "../Models/Lookups/StaticServiceProviderImportDatesModel";
import ScheduleWACCommand from "../Models/Queries/ScheduleWACForRawImportLogIDCommand";
import DeleteProcessingFileCommand from "../Models/Queries/DeleteProcessingFileCommand";
import AdhocWACCommand from "../Models/Commands/AdhocWACCommand";

export interface IHomeApiClient {
    getImportProcessStatus(): AxiosPromise<Array<Model.PlainObject<StaticServiceProviderImportDatesLookup>>>;
    interceptRawImportFileID(rawImportFileID: number) : AxiosPromise<Model.PlainObject<CommandResult>>;
    scheduleWACForRawImportLogID(command: ScheduleWACCommand) : AxiosPromise<Model.PlainObject<CommandResult>>;
    scheduleWacCommand(command: ScheduleWACCommand) : AxiosPromise<Model.PlainObject<CommandResult>>;
    runWeightedAverageCostAdHoc(command: Model.PlainObject<AdhocWACCommand>) : AxiosPromise<Model.PlainObject<CommandResult>>;
}

@injectable()
export default class HomeApiClient extends Data.ApiClientBase implements IHomeApiClient {

    constructor(config = AppService.get(Types.Config)) {
        super(`${config.apiPath}`)
    }

    public getImportProcessStatus(fileGroupingID?: number, importDate?: Date): AxiosPromise<Array<Model.PlainObject<StaticServiceProviderImportDatesLookup>>>{
        return this.axios.get(`${this.apiPath}/ProcessStatus/GetFileImportExportStatus`
            + (fileGroupingID !== 0 ? `?StaticServiceProviderFileGroupingID=${fileGroupingID}` : importDate !== null ? `?Date=${importDate?.toDateString()}`: ``)
            + (importDate !== null ? `&Date=${importDate?.toDateString()}` : ``));
    }

    public interceptRawImportFileID(rawImportFileID: number) : AxiosPromise<Model.PlainObject<CommandResult>>{
        return this.axios.post(`${this.apiPath}/InterceptData/InterceptRawImportFileID`, {rawImportFileID: rawImportFileID});
    }

    public normalizeRawImportFileID(rawImportFileID: number) : AxiosPromise<Model.PlainObject<CommandResult>>{
        return this.axios.post(`${this.apiPath}/NormalizeData/NormalizeRawImportFileID`, {rawImportFileID: rawImportFileID});
    }

    public extractRawImportFileId(rawImportFileId: number) : AxiosPromise<Model.PlainObject<CommandResult>>{
        return this.axios.post(`${this.apiPath}/ExtractData/ExtractRawImportFileId`, {rawImportFileId: rawImportFileId});
    }

    public deleteProcessingFile(command:  Model.PlainObject<DeleteProcessingFileCommand>) : AxiosPromise<Model.PlainObject<CommandResult>>{
        return this.axios.post(`${this.apiPath}/ProcessStatus/DeleteProcessingFile`, command);
    }

    public scheduleWACForRawImportLogID(command:  Model.PlainObject<ScheduleWACCommand>) : AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/WeightedAverageCost/ScheduleWACForRawImportLogID`, command);
    }

    public scheduleWacCommand(command: Model.PlainObject<ScheduleWACCommand>) : AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/WeightedAverageCost/ScheduleWacCommandWithAnyParameters`, command);
    }

    public resetRawImportFile(rawImportFileId: number): AxiosPromise<void> {
        return this.axios.post(`${this.apiPath}/ProcessStatus/ResetRawImportFileOperation`, rawImportFileId, {
            headers: { 'Content-Type': 'application/json' }
        });
    }

    public cancelRawImportFile(rawImportFileId: number): AxiosPromise<void>  {
        return this.axios.post(`${this.apiPath}/ProcessStatus/MarkRawImportFileAsFailed`, rawImportFileId, {
            headers: { 'Content-Type': 'application/json' }
        });
    }

    public runWeightedAverageCostAdHoc(command: Model.PlainObject<AdhocWACCommand>): AxiosPromise<Model.PlainObject<CommandResult>> {
        return this.axios.post(`${this.apiPath}/WeightedAverageCost/RunWeightedAverageCostAdHoc`, command);
    }
}